import type { Dispatch, ReactElement } from 'react'
import { Editable, ReactEditor, Slate, withReact } from 'slate-react'
import { Editor, Range, Transforms, createEditor } from 'slate'
import { createContext, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { withHistory } from 'slate-history'
import cc from 'classcat'

import QuoteButton, { quoteEventHandler } from './toolbar/QuoteButton'
import { Popover } from '../../../../Popover'
import { baseBlockElementsEventHandler } from './base_block_elements'
import { listEventHandler, withLists } from './lists'
import { parseHtml, serialize, withHeadlinePasting, withPasting } from './serialization'
import { useTranslate } from '../../../../../utils/translate'
import { withButtons } from './buttons'
import { withLinks } from './links'
import { withSingleLine, withUtils } from './utils'
import AlignButton from './toolbar/AlignButton'
import BaseBlockElementsDropdown from './toolbar/BaseBlockElementsDropdown'
import ButtonLinkElementButton from './toolbar/ButtonLinkElementButton'
import ColorButton from './toolbar/ColorButton'
import Element from './Element'
import Leaf from './Leaf'
import LinkButton from './toolbar/LinkButton'
import LinkTooltipButton from '../components/LinkTooltip/LinkTooltipButton'
import LinkTooltipText from '../components/LinkTooltip/LinkTooltipText'
import ListButton from './toolbar/ListButton'
import MarkButton from './toolbar/MarkButton'
import TooltipStateProvider from '../components/LinkTooltip/LinkTooltipContext'
import useEventHandler from '../../../../../utils/hooks/useEventHandler'

export type ToolbarContextType = [typeof initialToolbarState, Dispatch<{ type: string }>]

export const initialToolbarState = {
  isColorPickerOpen: false,
  isBaseBlockElementsDropdownOpen: false,
  isLinkTooltipOpen: false,
  isButtonLinkTooltipOpen: false,
}

export const ToolbarContext = createContext<ToolbarContextType>([initialToolbarState, () => undefined])

export function toolbarReducer(state, action: { type: string }) {
  switch (action.type) {
    case 'toggle_color_picker':
      return state.isColorPickerOpen
        ? { ...state, isColorPickerOpen: false }
        : {
            ...state,
            isLinkTooltipOpen: false,
            isButtonLinkTooltipOpen: false,
            isBaseBlockElementsDropdownOpen: false,
            isColorPickerOpen: true,
          }
    case 'close_color_picker':
      return { ...state, isColorPickerOpen: false }
    case 'toggle_base_block_elements_dropdown':
      return state.isBaseBlockElementsDropdownOpen
        ? { ...state, isBaseBlockElementsDropdownOpen: false }
        : {
            ...state,
            isLinkTooltipOpen: false,
            isButtonLinkTooltipOpen: false,
            isBaseBlockElementsDropdownOpen: true,
            isColorPickerOpen: false,
          }
    case 'close_base_block_elements_dropdown':
      return { ...state, isBaseBlockElementsDropdownOpen: false }
    case 'open_link_tooltip':
      return { ...state, isColorPickerOpen: false, isBaseBlockElementsDropdownOpen: false, isLinkTooltipOpen: true }
    case 'close_link_tooltip':
      return { ...state, isLinkTooltipOpen: false }
    case 'open_button_tooltip':
      return {
        ...state,
        isColorPickerOpen: false,
        isBaseBlockElementsDropdownOpen: false,
        isButtonLinkTooltipOpen: true,
      }
    case 'close_button_tooltip':
      return { ...state, isButtonLinkTooltipOpen: false }
    case 'reset':
      return initialToolbarState
    default:
      throw new Error()
  }
}

type Props = {
  html: string
  isHeadline: boolean
  onChange: (string) => void
  onFocus: () => void
  setRef: (ref: object) => void
}

export default function RichTextEditor({
  html,
  isHeadline,
  onChange,
  onFocus,
  setRef,
}: Readonly<Props>): ReactElement<any, any> | null {
  const [value, setValue] = useState(parseHtml(html))
  const [lastMouseDownTarget, setLastMouseDownTarget] = useState(null)
  const [toolbarState, toolbarDispatch] = useReducer(toolbarReducer, initialToolbarState)
  const editableRef = useRef<HTMLDivElement | null>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const editor = useMemo(() => {
    const createdEditor = withHistory(
      withUtils(withLinks(withButtons(withLists(withPasting(withReact(createEditor())))))),
    )
    if (isHeadline) withHeadlinePasting(withSingleLine(createdEditor))
    return createdEditor
  }, [isHeadline])

  const t = useTranslate('interface')

  // Note that this component shares the same translation namespace as the
  // `RichTextField` component. If a key is renamed, it needs to be updated in
  // both components.
  const scopedT = (key) => t(`components.richTextComponent.${key}`)
  const renderElement = useCallback((props) => <Element {...props} />, [])
  const renderLeaf = useCallback((props) => <Leaf {...props} />, [])

  const isFocused =
    lastMouseDownTarget &&
    (editableRef.current?.contains(lastMouseDownTarget) || toolbarRef.current?.contains(lastMouseDownTarget))
  const isAnyTooltipOpen =
    toolbarState.isColorPickerOpen ||
    toolbarState.isBaseBlockElementsDropdownOpen ||
    toolbarState.isLinkTooltipOpen ||
    toolbarState.isButtonLinkTooltipOpen

  useEffect(() => {
    // Triggering normalization on initial value.
    // See: https://github.com/ianstormtaylor/slate/issues/3465#issuecomment-655592962
    editor.children = value
    Editor.normalize(editor, { force: true })
    setValue(editor.children)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRef({
      focus: () => {
        // Selecting the whole first line first.
        Transforms.select(editor, [0])
        // Then use that selection to find out the start point of the editor.
        if (editor.selection) Transforms.select(editor, Range.start(editor.selection))
        ReactEditor.focus(editor)
      },
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEventHandler('mousedown', (e) => {
    setLastMouseDownTarget(e.target)

    if (editableRef.current?.contains(e.target) && !isFocused) {
      ReactEditor.focus(editor)
      updateOnFocus()
    }
  })

  useEventHandler('click', () => {
    // Use the clicks' mousedown event target to avoid blurring when the mouse
    // has been moved outside the editor before the mouseup event.
    if (!isFocused) {
      const newHtml = serialize(value)

      if (html !== newHtml) onChange(newHtml)
      else toolbarDispatch({ type: 'reset' })
    }
  })

  const updateOnFocus = () => {
    onFocus()
  }

  const toolbarContext = useMemo<ToolbarContextType>(() => [toolbarState, toolbarDispatch], [toolbarState])

  return (
    <ToolbarContext.Provider value={toolbarContext}>
      <Slate editor={editor} initialValue={value} onChange={(value) => setValue([...value])}>
        <Popover
          open={isFocused || isAnyTooltipOpen}
          side="top"
          sideOffset={5}
          avoidCollisions
          hideArrow
          usePortal
          zIndex={0}
          className="dali-plugin-text-toolbar-popover"
          collisionPadding={20}
          anchor={
            <div ref={editableRef}>
              <Editable
                className="dali-plugin-text-content-editable"
                renderLeaf={renderLeaf}
                renderElement={renderElement}
                onFocus={updateOnFocus}
                // Note that if the placeholder key is renamed, it also needs to
                // be updated in the `RichTextField` component (see note above).
                placeholder={
                  isHeadline
                    ? scopedT('textInputField.headlinePlaceholder')
                    : scopedT('textInputField.standardPlaceholder')
                }
                onKeyDown={
                  isHeadline
                    ? undefined
                    : (event) => {
                        listEventHandler?.(editor, event)
                        quoteEventHandler?.(editor, event)
                        baseBlockElementsEventHandler?.(editor, event)
                      }
                }
              />
              <TooltipStateProvider>
                <LinkTooltipText editableRef={editableRef} />
                <LinkTooltipButton editableRef={editableRef} />
              </TooltipStateProvider>
            </div>
          }
        >
          <div
            ref={toolbarRef}
            className={cc([
              'dali-plugin-text-toolbar',
              { 'dali-plugin-text-toolbar-active': isFocused || isAnyTooltipOpen },
            ])}
          >
            <BaseBlockElementsDropdown t={scopedT} isHeadline={isHeadline} />
            <MarkButton title={scopedT('boldButton.tooltip')} format="bold" />
            <MarkButton title={scopedT('italicButton.tooltip')} format="italic" />
            <MarkButton title={scopedT('underlineButton.tooltip')} format="underline" />
            {!isHeadline && (
              <>
                <QuoteButton title={scopedT('blockquoteButton.tooltip')} />
                <LinkButton title={scopedT('linkButton.tooltip')} format="link" />
                <LinkButton title={scopedT('removeHyperlinkButton.tooltip')} format="unlink" />
                <ListButton title={scopedT('insertNumberedListButton.tooltip')} format="ordered-list" />
                <ListButton title={scopedT('insertBulletListButton.tooltip')} format="unordered-list" />
              </>
            )}
            <AlignButton t={scopedT} format="left" />
            <AlignButton t={scopedT} format="center" />
            <AlignButton t={scopedT} format="right" />
            {!isHeadline && (
              <>
                <ButtonLinkElementButton title={scopedT('buttonLinkElementButton.tooltip')} format="button" />
                <ButtonLinkElementButton title={scopedT('removeButtonLinkElementButton.tooltip')} format="unbutton" />
              </>
            )}
            <ColorButton editableRef={editableRef} />
          </div>
        </Popover>
      </Slate>
    </ToolbarContext.Provider>
  )
}
